/**
 * Format field values for display in the confirmation/review step.
 * Handles credential masking, type-specific formatting, and label lookup.
 */

import type { ConnectorFieldSchema } from '../../types/connector-schema';

/**
 * Format a field value for display in the confirmation step.
 */
export function formatFieldValue(
  field: ConnectorFieldSchema,
  value: unknown
): string {
  // Not set
  if (value === undefined || value === null || value === '') {
    return 'Not set';
  }

  // Credential masking
  if (field.is_credential || field.type === 'password') {
    return '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'; // ••••••••
  }

  // Boolean
  if (field.type === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Multi-select checkbox — show labels instead of values
  if (field.type === 'multi_select_checkbox' && Array.isArray(value)) {
    if (value.length === 0) return 'None selected';
    if (field.checkbox_options) {
      const labelMap = new Map(
        field.checkbox_options.map((opt) => [opt.value, opt.label])
      );
      return value
        .map((v) => labelMap.get(String(v)) ?? String(v))
        .join(', ');
    }
    return value.join(', ');
  }

  // Select — show label instead of value
  if (field.type === 'select' && field.options) {
    const option = field.options.find((opt) => opt.value === String(value));
    return option ? option.label : String(value);
  }

  // OAuth button — the value is a credentials object from the OAuth flow.
  // Show "Connected" instead of dumping the object as "[object Object]".
  if (field.type === 'oauth_button') {
    return 'Connected';
  }

  // File — show filename
  if (field.type === 'file') {
    if (typeof value === 'object' && value !== null && 'name' in value) {
      return (value as { name: string }).name;
    }
    return 'File uploaded';
  }

  // Safety net: if the value is a non-null object or array that wasn't
  // handled by a type-specific branch above, avoid rendering [object Object].
  if (typeof value === 'object' && value !== null) {
    return Array.isArray(value) ? value.join(', ') : JSON.stringify(value);
  }

  // Default: convert to string
  return String(value);
}
